import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'bjp_admin_token';
const INCLUDE_CREDENTIALS = new URL(BASE).origin === window.location.origin;

export const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: INCLUDE_CREDENTIALS });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  api.post('/auth/logout').catch(() => {});
};

export const login = async (email, password) => {
  const { data } = await api.post('/auth/login', { email, password });
  setToken(data.token);
  return data;
};

export const me = async () => {
  const { data } = await api.get('/auth/me');
  return data;
};

// Scholars
export const listScholars = async () => (await api.get('/scholars')).data;
export const createScholar = async (payload) => (await api.post('/scholars', payload)).data;
export const updateScholar = async (id, payload) => (await api.put(`/scholars/${id}`, payload)).data;
export const deleteScholar = async (id) => (await api.delete(`/scholars/${id}`)).data;

// Apparel Orders
export const createOrder = async (payload) => (await api.post('/orders', payload)).data;
export const startCheckout = async (payload) => (await api.post('/payments/checkout', { ...payload, origin_url: window.location.origin })).data;
export const getPaymentStatus = async (sessionId) => (await api.get(`/payments/status/${sessionId}`)).data;
export const getRsvpSummary = async (slug) => (await api.get(`/fundraisers/${slug}/rsvp-summary`)).data;
export const submitRsvp = async (slug, payload) => (await api.post(`/fundraisers/${slug}/rsvp`, payload)).data;
export const listRsvps = async (slug) => (await api.get(`/admin/fundraisers/${slug}/rsvps`)).data;
export const deleteRsvp = async (slug, id) => (await api.delete(`/admin/fundraisers/${slug}/rsvps/${id}`)).data;
export const listRegistrations = async () => (await api.get('/admin/registrations')).data;
export const downloadRegistrationsCsv = async () => (await api.get('/admin/registrations.csv', { responseType: 'blob' })).data;
export const getRegistrationEmailStatus = async () => (await api.get('/admin/registrations/email-status')).data;
export const sendWelcomeEmail = async (id) => (await api.post(`/admin/registrations/${id}/welcome-email`)).data;
export const getFundraiserSettings = async (slug) => (await api.get(`/fundraisers/${slug}/settings`)).data;
export const getStaffFundraiserSettings = async (slug) => (await api.get(`/admin/fundraisers/${slug}/settings`)).data;
export const saveFundraiserSettings = async (slug, payload) => (await api.put(`/admin/fundraisers/${slug}/settings`, payload)).data;
export const validatePromo = async (payload) => (await api.post('/orders/validate-promo', payload)).data;
export const getLimitedStock = async (productId, limit = 20) =>
  (await api.get(`/products/${productId}/limited-stock`, { params: { limit } })).data;
export const listOrders = async () => (await api.get('/orders')).data;
export const markOrderPaid = async (id) => (await api.patch(`/orders/${id}/paid`)).data;
export const deleteOrder = async (id) => (await api.delete(`/orders/${id}`)).data;
export const resendOrderReceipts = async (id) => (await api.post(`/orders/${id}/resend-receipts`)).data;

// Inventory
export const listInventory = async () => (await api.get('/inventory')).data;
export const upsertInventory = async (payload) => (await api.post('/inventory', payload)).data;

// Product Prices
export const listProductPrices = async () => (await api.get('/products/prices')).data;
export const setProductPrice = async (productId, price) =>
  (await api.put(`/products/prices/${productId}`, { price })).data;


// Championship Rosters
export const listChampionships = async () => (await api.get('/championships')).data;
export const getChampionship = async (teamId) => (await api.get(`/championships/${teamId}`)).data;
export const updateChampionshipMeta = async (teamId, meta) =>
  (await api.put(`/championships/${teamId}`, meta)).data;
export const addChampionshipPlayer = async (teamId, player) =>
  (await api.post(`/championships/${teamId}/players`, player)).data;
export const editChampionshipPlayer = async (teamId, playerId, player) =>
  (await api.patch(`/championships/${teamId}/players/${playerId}`, player)).data;

// Contributors + submissions
export const listContributors = async () => (await api.get('/contributors')).data;
export const createContributor = async (payload) => (await api.post('/contributors', payload)).data;
export const patchContributor = async (cid, payload) => (await api.patch(`/contributors/${cid}`, payload)).data;
export const deleteContributor = async (cid) => (await api.delete(`/contributors/${cid}`)).data;
export const getContributorByToken = async (token) => (await api.get(`/contribute/${token}`)).data;
export const submitViaToken = async (token, payload) => (await api.post(`/contribute/${token}/submissions`, payload)).data;
export const listSubmissionsAdmin = async () => (await api.get('/admin/submissions')).data;
export const patchSubmission = async (sid, payload) => (await api.patch(`/admin/submissions/${sid}`, payload)).data;
export const deleteSubmission = async (sid) => (await api.delete(`/admin/submissions/${sid}`)).data;
export const listApprovedSubmissions = async () => (await api.get('/submissions/approved')).data;
export const listCalendarEvents = async () => (await api.get('/calendar/events')).data;
export const listAdminCalendarEvents = async () => (await api.get('/admin/calendar/events')).data;
export const createCalendarEvent = async (payload) => (await api.post('/admin/calendar/events', payload)).data;
export const updateCalendarEvent = async (id, payload) => (await api.patch(`/admin/calendar/events/${id}`, payload)).data;
export const deleteCalendarEvent = async (id) => (await api.delete(`/admin/calendar/events/${id}`)).data;
export const listFootballPracticeTimes = async () => (await api.get('/football-practice-times')).data;
export const listAdminFootballPracticeTimes = async () => (await api.get('/admin/football-practice-times')).data;
export const createFootballPracticeTime = async (payload) => (await api.post('/admin/football-practice-times', payload)).data;
export const updateFootballPracticeTime = async (id, payload) => (await api.patch(`/admin/football-practice-times/${id}`, payload)).data;
export const deleteFootballPracticeTime = async (id) => (await api.delete(`/admin/football-practice-times/${id}`)).data;
export const listFootballPracticeCollage = async () => (await api.get('/football-practice-collage')).data;
export const listAdminFootballPracticeCollage = async () => (await api.get('/admin/football-practice-collage')).data;
export const createFootballPracticePhoto = async (payload) => (await api.post('/admin/football-practice-collage', payload)).data;
export const reorderFootballPracticePhotos = async (photoIds) => (await api.patch('/admin/football-practice-collage/order', { photoIds })).data;
export const updateFootballPracticePhoto = async (id, payload) => (await api.patch(`/admin/football-practice-collage/${id}`, payload)).data;
export const deleteFootballPracticePhoto = async (id) => (await api.delete(`/admin/football-practice-collage/${id}`)).data;
export const listPublicAnnouncements = async () => (await api.get('/announcements')).data;
export const listGameResults = async () => (await api.get('/game-results')).data;
export const getKickoffWeather = async (gameId) => (await api.get(`/weather/game/${gameId}`)).data;
export const postGameResult = async (id, payload) => (await api.put(`/admin/football-practice-times/${id}/result`, payload)).data;
export const clearGameResult = async (id) => (await api.delete(`/admin/football-practice-times/${id}/result`)).data;
export const changePassword = async (payload) => (await api.post('/auth/change-password', payload)).data;
export const clearCoachThread = async (sessionId, assistant) => (await api.delete(`/coach/chat/${sessionId}`, { params: { assistant } })).data;
export const uploadGamePhoto = async (file, onProgress) => {
  const form = new FormData();
  form.append('file', file);
  return (await api.post('/admin/game-photos', form, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) })).data;
};
export const listPowerRankings = async () => (await api.get('/power-rankings')).data;
export const listAdminPowerRankings = async () => (await api.get('/admin/power-rankings')).data;
export const upsertPowerRanking = async (payload) => (await api.post('/admin/power-rankings', payload)).data;
export const deletePowerRanking = async (id) => (await api.delete(`/admin/power-rankings/${id}`)).data;
export const getBrooklineWeather = async () => (await api.get('/weather/brookline')).data;

// Coach Accounts
export const listCoachAccounts = async () => (await api.get('/admin/coach-accounts')).data;
export const createCoachAccount = async (payload) => (await api.post('/admin/coach-accounts', payload)).data;
export const updateCoachAccountStatus = async (id, active) => (await api.patch(`/admin/coach-accounts/${id}/status`, { active })).data;
export const resetCoachAccountPassword = async (id) => (await api.post(`/admin/coach-accounts/${id}/reset-password`, {})).data;

// Privacy-friendly site visitor count
export const registerVisitor = async () => (await api.post('/visitors/register')).data;

export const deleteChampionshipPlayer = async (teamId, playerId) =>
  (await api.delete(`/championships/${teamId}/players/${playerId}`)).data;
export const addChampionshipCoach = async (teamId, coach) =>
  (await api.post(`/championships/${teamId}/coaches`, coach)).data;
export const editChampionshipCoach = async (teamId, coachId, coach) =>
  (await api.patch(`/championships/${teamId}/coaches/${coachId}`, coach)).data;
export const deleteChampionshipCoach = async (teamId, coachId) =>
  (await api.delete(`/championships/${teamId}/coaches/${coachId}`)).data;

// Alumni Rotating Gallery
export const getAlumniGallery = async (slug) => (await api.get(`/alumni-gallery/${slug}`)).data;
export const addAlumniPhoto = async (slug, photo) => (await api.post(`/alumni-gallery/${slug}/photos`, photo)).data;
export const editAlumniPhoto = async (slug, photoId, photo) => (await api.patch(`/alumni-gallery/${slug}/photos/${photoId}`, photo)).data;
export const deleteAlumniPhoto = async (slug, photoId) => (await api.delete(`/alumni-gallery/${slug}/photos/${photoId}`)).data;
